// src/components/ownership/OwnershipTab.jsx
// OWNERSHIP-CAPTURE-ARC — new file. Replaces StockPage.jsx's "Insiders" Coming
// Soon placeholder with real content, once edgarInsiderPipeline.js has actually
// run — see the explicit "no data yet" state below for the honest interim case.
//
// Structure deliberately mirrors TickerFilingsSummary.jsx (confirmed real file,
// same session) — same loading/error/empty shape, same visual language (row
// borders, color palette from shared.js) — so the Ownership and Filings tabs
// feel like one design system, not two.
//
// Institutional (13F) fields render only when populated — institutionalHoldings-
// ComputePipeline.js doesn't exist yet, so snapshot.institutionalOwnershipPct
// etc. will be null for every ticker until that ships. This component doesn't
// hide the Institutional section entirely in that case — it shows a small
// "not yet available" note instead, so members can see what's coming rather
// than wondering if something's broken.

import React from 'react';
import { useTickerOwnership } from '../../hooks/useTickerOwnership';

function fmtPct(n) {
  if (n === null || n === undefined) return null;
  return `${Number(n) >= 0 ? '+' : ''}${Number(n).toFixed(2)}%`;
}

function InsiderOwnershipCard({ snapshot }) {
  const hasInsiderData = snapshot && (snapshot.INSIDEROWNERSHIPPCT !== null && snapshot.INSIDEROWNERSHIPPCT !== undefined);
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '12px', padding: '1.1rem', marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#f8fafc', marginBottom: '0.75rem' }}>
        👤 Insider Ownership
      </div>
      {hasInsiderData ? (
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.2rem' }}>Ownership</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#00d4ff' }}>
              {Number(snapshot.INSIDEROWNERSHIPPCT).toFixed(2)}%
            </div>
          </div>
          {snapshot.INSIDERTRANSACTIONSPCT !== null && snapshot.INSIDERTRANSACTIONSPCT !== undefined && (
            <div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.2rem' }}>Net Change (90d)</div>
              <div style={{ fontSize: '1.15rem', fontWeight: 800,
                color: Number(snapshot.INSIDERTRANSACTIONSPCT) >= 0 ? '#00ff88' : '#ff4444' }}>
                {fmtPct(snapshot.INSIDERTRANSACTIONSPCT)}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '0.78rem', color: '#475569' }}>No insider ownership data yet for this ticker.</div>
      )}
    </div>
  );
}

function InstitutionalOwnershipCard({ snapshot }) {
  const hasInstData = snapshot && (snapshot.INSTITUTIONALOWNERSHIPPCT !== null && snapshot.INSTITUTIONALOWNERSHIPPCT !== undefined);
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '12px', padding: '1.1rem', marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#f8fafc', marginBottom: '0.75rem' }}>
        🏦 Institutional Ownership (13F)
      </div>
      {hasInstData ? (
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.2rem' }}>Ownership</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#00d4ff' }}>
              {Number(snapshot.INSTITUTIONALOWNERSHIPPCT).toFixed(2)}%
            </div>
          </div>
          {snapshot.INSTITUTIONAL13FOWNERSHIPCHANGEPCT !== null && (
            <div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.2rem' }}>QoQ Change</div>
              <div style={{ fontSize: '1.15rem', fontWeight: 800,
                color: Number(snapshot.INSTITUTIONAL13FOWNERSHIPCHANGEPCT) >= 0 ? '#00ff88' : '#ff4444' }}>
                {fmtPct(snapshot.INSTITUTIONAL13FOWNERSHIPCHANGEPCT)}
              </div>
            </div>
          )}
          {snapshot.INSTITUTIONALDISTINCTFILERCOUNT !== null && (
            <div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.2rem' }}>Filers</div>
              <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#f8fafc' }}>
                {snapshot.INSTITUTIONALDISTINCTFILERCOUNT}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '0.78rem', color: '#475569' }}>
          Not yet available — 13F capture is scoped, not built yet.
        </div>
      )}
    </div>
  );
}

function TransactionRow({ txn }) {
  const isBuy = txn.ACQUIREDDISPOSEDCODE === 'A';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0',
      borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.8rem', color: '#f8fafc', fontWeight: 700, flex: '1 1 160px' }}>
        {txn.REPORTINGOWNERNAME}
        {txn.OFFICERTITLE && (
          <span style={{ color: '#64748b', fontWeight: 500, fontSize: '0.72rem' }}> — {txn.OFFICERTITLE}</span>
        )}
      </span>
      <span style={{ fontSize: '0.75rem', fontWeight: 700,
        color: isBuy ? '#00ff88' : '#ff4444', flex: '0 0 70px' }}>
        {isBuy ? 'BUY' : 'SELL'}
      </span>
      <span style={{ fontSize: '0.75rem', color: '#94a3b8', flex: '0 0 100px' }}>
        {txn.TRANSACTIONSHARES ? Number(txn.TRANSACTIONSHARES).toLocaleString() + ' sh' : '—'}
      </span>
      <span style={{ fontSize: '0.75rem', color: '#64748b', flex: '0 0 90px', textAlign: 'right' }}>
        {txn.TRANSACTIONDATE}
      </span>
      {txn.SOURCEURL && (
        <a href={txn.SOURCEURL} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: '0.72rem', fontWeight: 700, color: '#00ff88', textDecoration: 'none' }}>
          View ↗
        </a>
      )}
    </div>
  );
}

export default function OwnershipTab({ ticker, token }) {
  const { hasData, locked, degraded, reason, snapshot, transactions, loading, error } = useTickerOwnership(ticker, token);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading ownership data…</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '2rem', color: '#ff4444' }}>{error}</div>;

  // Genuinely an edge case, not the main guest path — StockPage.jsx's
  // LockedTabGate already intercepts guests before this component ever
  // mounts. This only renders if client-side auth state is briefly stale
  // (e.g. a token that expired between page load and this fetch). Defense
  // in depth, matching ownershipQueryService.js's own server-side gate —
  // never assume the client-side lock alone is sufficient.
  if (locked) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1.5rem', background: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔒</div>
        <div style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: '380px', margin: '0 auto' }}>
          {reason || 'Sign in to view ownership data.'}
        </div>
      </div>
    );
  }

  // Technical degradation (Oracle pool unavailable) — distinct from "no data
  // yet" so a member isn't told a real capture gap is a database outage, or
  // vice versa. Same three-state discipline as ownershipQueryService.js.
  if (degraded) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#ff4444' }}>
        Ownership data is temporarily unavailable. Please try again shortly.
      </div>
    );
  }

  if (!hasData) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1.5rem', background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.05)', borderRadius: '14px' }}>
        <div style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>📊</div>
        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.4rem' }}>
          No Ownership Data Yet
        </div>
        <div style={{ fontSize: '0.78rem', color: '#475569', maxWidth: '380px', margin: '0 auto' }}>
          {reason || 'Ownership data has not been captured for this ticker yet.'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <InsiderOwnershipCard snapshot={snapshot} />
      <InstitutionalOwnershipCard snapshot={snapshot} />

      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#f8fafc', marginBottom: '0.5rem' }}>
          Recent Insider Transactions
        </div>
        {transactions.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: '#475569', fontSize: '0.85rem' }}>
            No individual transactions on record yet.
          </div>
        ) : (
          transactions.map((txn, i) => <TransactionRow key={txn.TRANSACTIONDATE + i} txn={txn} />)
        )}
      </div>

      <div style={{ fontSize: '0.68rem', color: '#475569', marginTop: '1rem' }}>
        * Insider transactions from SEC Form 4 filings. Institutional (13F) data coming soon.
      </div>
    </div>
  );
}
